// Which inactive layer plays next, and which one takes a manual switch target.
// Layers are compared by the playlist position of their content, not by their
// place in the DOM, so the order survives jumps to arbitrary positions.

export type LayerCandidate = {
    index: number | null; // playlist position of the loaded content
    ready: boolean; // loaded and able to take over right away
    stale: boolean; // loaded before the last manual switch
};

// Steps from one playlist position to another, wrapping around. The same
// position counts as a full round, so it comes last.
export function distance(from: number, to: number, length: number): number {
    if (length <= 0) return 0;
    return (((to - from) % length) + length) % length || length;
}

// Playback order after the current position. Content from before the last
// manual switch only comes after everything loaded since.
export function sortBySequence<T>(
    layers: T[],
    describe: (layer: T) => LayerCandidate,
    current: number | null,
    length: number,
): T[] {
    const rank = (layer: T) => {
        const { index, stale } = describe(layer);
        const steps =
            index === null || current === null
                ? length + 1
                : distance(current, index, length);
        return (stale ? length + 2 : 0) + steps;
    };

    return layers
        .map((layer, position) => ({ layer, position, rank: rank(layer) }))
        .sort((a, b) => a.rank - b.rank || a.position - b.position)
        .map(({ layer }) => layer);
}

// The ready layer that continues the sequence after the current position.
export function pickNext<T>(
    layers: T[],
    describe: (layer: T) => LayerCandidate,
    current: number | null,
    length: number,
): T | null {
    return (
        sortBySequence(layers, describe, current, length).find(
            (layer) => describe(layer).ready,
        ) ?? null
    );
}

// The layer to load a manual switch target into: one that is not ready
// anyway, otherwise the one that would play last. The next one stays as a
// reserve against a black screen.
export function pickSwitchTarget<T>(
    layers: T[],
    describe: (layer: T) => LayerCandidate,
    current: number | null,
    length: number,
): T | null {
    const notReady = layers.find((layer) => !describe(layer).ready);
    if (notReady !== undefined) return notReady;

    const sorted = sortBySequence(layers, describe, current, length);
    return sorted.length > 1 ? sorted[sorted.length - 1] : (sorted[0] ?? null);
}
